import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { User, LogOut, Users, Shield, UserPlus, Target, Flame, Play, Award, ChevronRight, Instagram, Check, Settings } from 'lucide-react';

import { ClubBadge } from '@/components/ClubBadge';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AdminNotificationBell } from '@/components/AdminNotificationBell';
import { OnboardingTutorial } from '@/components/OnboardingTutorial';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useScoutCooldown } from '@/hooks/useScoutCooldown';
import { isOnCooldown } from '@/lib/cooldown';
import { calculateLevel, getLevelProgress, MAX_LEVEL } from '@/lib/levels';
import { PlayerBadges } from '@/components/PlayerBadges';

interface Profile {
  full_name: string;
  xp: number;
  level: number;
}

interface DiscoverPlayer {
  id: string;
  full_name: string;
  club: string;
  position: string;
  nationality: string;
  date_of_birth: string;
  league: string | null;
}

interface DiscoverSlot {
  player: DiscoverPlayer;
  seq: number;
}

interface OpenSession {
  id: string;
  player_id: string;
  player_name: string;
  ratings_count: number;
}

interface FeaturedPlayer {
  slot: number;
  feature_date: string;
  id: string;
  full_name: string;
  nationality: string;
  date_of_birth: string;
  player_position: string;
  club: string;
  league: string | null;
  overall: number;
}


const WEEKLY_GOAL = 5;
const DISCOVER_VISIBLE = 3;
const DISCOVER_BATCH = 24;
const DISCOVER_POOL_REFILL = 6;
const DISCOVER_ROTATE_MS = 60_000;
const TOTAL_ATTRIBUTES = 28;

function startOfWeek(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay(); // 0 Sun .. 6 Sat
  const diff = (day === 0 ? -6 : 1 - day); // shift to Monday
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getPositionStyle(position: string) {
  const styles: Record<string, string> = {
    goalkeeper: 'bg-pos-gk/10 text-pos-gk border-pos-gk/30',
    defender: 'bg-pos-def/10 text-pos-def border-pos-def/30',
    midfielder: 'bg-pos-mid/10 text-pos-mid border-pos-mid/30',
    forward: 'bg-pos-fwd/10 text-pos-fwd border-pos-fwd/30',
  };
  return styles[position] || 'bg-muted text-muted-foreground';
}

const calculateAge = (dob: string) => {
  if (!dob) return '—';
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

export default function Dashboard() {
  const [now, setNow] = useState(Date.now());
  const [clubs, setClubs] = useState<string[]>([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [scoutedCount, setScoutedCount] = useState(0);
  const [scoutedThisWeek, setScoutedThisWeek] = useState(0);
  const [badgesCount, setBadgesCount] = useState(0);
  const [openSession, setOpenSession] = useState<OpenSession | null>(null);
  const [featured, setFeatured] = useState<FeaturedPlayer[]>([]);
  const [discoverSlots, setDiscoverSlots] = useState<DiscoverSlot[]>([]);
  const [discoverOveralls, setDiscoverOveralls] = useState<Record<string, number>>({});
  void now; // force re-render each minute for the live countdown
  const poolRef = useRef<DiscoverPlayer[]>([]);
  const seqRef = useRef(0);
  const fetchingPoolRef = useRef(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000); // tick every minute
    return () => clearInterval(t);
  }, []);

  const timeUntilMidnight = () => {
    const d = new Date();
    const midnight = new Date(d);
    midnight.setHours(24, 0, 0, 0);
    const diff = midnight.getTime() - d.getTime();
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  };

  const isSameCalendarDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const featuredTodayId = featured.find((f) => f.slot === 0)?.id;
  const { endsAt: featuredCooldownEndsAt, createdAt: featuredCreatedAt } = useScoutCooldown(featuredTodayId);
  const featuredScoutedToday = featuredCreatedAt && isSameCalendarDay(featuredCreatedAt, new Date());
  const featuredOnCooldown = isOnCooldown(featuredCooldownEndsAt);

  const discoverIdsKey = discoverSlots.map((s) => s.player.id).join(',');
  useEffect(() => {
    const ids = discoverSlots.map((s) => s.player.id);
    if (ids.length === 0) return;
    let cancelled = false;
    supabase
      .rpc('get_players_overall', { p_player_ids: ids })
      .then(({ data }) => {
        if (cancelled || !data) return;
        const m: Record<string, number> = {};
        (data as any[]).forEach((r) => { m[r.player_id] = r.overall; });
        setDiscoverOveralls((prev) => ({ ...prev, ...m }));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverIdsKey]);

  useEffect(() => {
    fetchPlayerCount();
    fetchProfile();
    checkOnboarding();
    fetchWeeklyGoal();
    fetchBadgesCount();
    fetchOpenSession();
    initDiscover();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    (async () => {
      const { data, error } = await (supabase as any).rpc('get_featured_queue', { p_count: 4 });
      if (!error && data) setFeatured(data as FeaturedPlayer[]);
    })();
  }, []);

  useEffect(() => {
    if (discoverSlots.length < DISCOVER_VISIBLE) return;
    const interval = setInterval(() => {
      rotateDiscover();
    }, DISCOVER_ROTATE_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverSlots.length]);

  const fetchPlayerCount = async () => {
    const { count } = await supabase
      .from('players')
      .select('*', { count: 'exact', head: true });
    setPlayerCount(count ?? 0);
  };

  const fetchWeeklyGoal = async () => {
    if (!user) return;
    const since = startOfWeek(new Date()).toISOString();
    const { count } = await supabase
      .from('scouting_sessions')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('completed', true)
      .gte('created_at', since);
    setScoutedThisWeek(count ?? 0);
  };

  const fetchBadgesCount = async () => {
    if (!user) return;
    const { count } = await supabase
      .from('user_badges')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id);
    setBadgesCount(count ?? 0);
  };

  const fetchOpenSession = async () => {
    if (!user) return;
    const { data: sessions } = await supabase
      .from('scouting_sessions')
      .select('id, player_id, created_at')
      .eq('user_id', user.id)
      .eq('completed', false)
      .order('created_at', { ascending: false })
      .limit(1);
    const s = sessions?.[0];
    if (!s) { setOpenSession(null); return; }
    const [{ data: player }, { count: ratingsCount }] = await Promise.all([
      supabase.from('players').select('full_name').eq('id', s.player_id).single(),
      supabase
        .from('scouting_ratings')
        .select('id', { count: 'exact', head: true })
        .eq('session_id', s.id),
    ]);
    setOpenSession({
      id: s.id,
      player_id: s.player_id,
      player_name: player?.full_name ?? 'Unknown player',
      ratings_count: ratingsCount ?? 0,
    });
  };

  const fetchDiscoverBatch = async (): Promise<DiscoverPlayer[]> => {
    const { data, error } = await (supabase as any).rpc('get_random_players', { n: DISCOVER_BATCH });
    if (error || !data) return [];
    return data as DiscoverPlayer[];
  };

  const refillPoolIfNeeded = async (excludeIds: Set<string>) => {
    if (poolRef.current.length >= DISCOVER_POOL_REFILL) return;
    if (fetchingPoolRef.current) return;
    fetchingPoolRef.current = true;
    try {
      const batch = await fetchDiscoverBatch();
      const fresh = batch.filter((p) => !excludeIds.has(p.id));
      poolRef.current = [...poolRef.current, ...fresh];
    } finally {
      fetchingPoolRef.current = false;
    }
  };

  const initDiscover = async () => {
    const batch = await fetchDiscoverBatch();
    if (batch.length === 0) return;
    const initial = batch.slice(0, DISCOVER_VISIBLE);
    poolRef.current = batch.slice(DISCOVER_VISIBLE);
    seqRef.current = initial.length;
    setDiscoverSlots(initial.map((p, i) => ({ player: p, seq: i })));
  };

  const rotateDiscover = async () => {
    const currentIds = new Set(discoverSlots.map((s) => s.player.id));
    await refillPoolIfNeeded(currentIds);
    const next = poolRef.current.find((p) => !currentIds.has(p.id));
    if (!next) return;
    poolRef.current = poolRef.current.filter((p) => p.id !== next.id);

    setDiscoverSlots((prev) => {
      if (prev.length === 0) return prev;
      let oldestIdx = 0;
      for (let i = 1; i < prev.length; i++) {
        if (prev[i].seq < prev[oldestIdx].seq) oldestIdx = i;
      }
      const nextSeq = seqRef.current++;
      const copy = prev.slice();
      copy[oldestIdx] = { player: next, seq: nextSeq };
      return copy;
    });
  };

  const fetchClubs = async () => {
    const PAGE_SIZE = 1000;
    const allClubs = new Set<string>();
    let from = 0;
    let hasMore = true;
    while (hasMore) {
      const { data, error } = await supabase
        .from('players')
        .select('club')
        .range(from, from + PAGE_SIZE - 1);
      if (error || !data) break;
      data.forEach((p) => p.club && allClubs.add(p.club));
      hasMore = data.length === PAGE_SIZE;
      from += PAGE_SIZE;
    }
    setClubs([...allClubs].sort());
  };

  const fetchProfile = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .select('full_name, xp, level')
      .eq('user_id', user.id)
      .single();
    if (!error && data) setProfile(data);
  };

  const checkOnboarding = async () => {
    if (!user) { setOnboardingChecked(true); return; }

    // scouted count (kept for display)
    const { data: sessions } = await supabase
      .from('scouting_sessions')
      .select('player_id')
      .eq('user_id', user.id)
      .eq('completed', true);
    if (sessions) {
      const uniquePlayers = new Set(sessions.map((s) => s.player_id));
      setScoutedCount(uniquePlayers.size);
    }

    // show onboarding ONLY if the user has not dismissed/completed it before
    const { data: prof } = await supabase
      .from('profiles')
      .select('onboarding_completed')
      .eq('user_id', user.id)
      .single();
    if (!prof?.onboarding_completed) {
      await fetchClubs();
      setShowOnboarding(true);
    }

    setOnboardingChecked(true);
  };

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    if (user) {
      await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('user_id', user.id);
    }
  };

  const progress = getLevelProgress(profile?.xp || 0);
  const level = calculateLevel(profile?.xp || 0);
  const weeklyPercent = Math.min(100, Math.round((scoutedThisWeek / WEEKLY_GOAL) * 100));

  return (
    <div className="min-h-screen bg-background">
      {/* Onboarding Tutorial */}
      {showOnboarding && onboardingChecked && (
        <Sentry.ErrorBoundary
          fallback={({ resetError }) => (
            <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center p-4">
              <div className="max-w-sm w-full text-center space-y-6">
                <h2 className="text-2xl font-display font-bold">Let's get you started</h2>
                <p className="text-muted-foreground">
                  Something hiccupped during the intro — no worries. You're all set to explore.
                </p>
                <Button
                  onClick={() => {
                    resetError();
                    handleOnboardingComplete();
                  }}
                  className="h-11 w-full"
                >
                  Continue to your dashboard
                </Button>
              </div>
            </div>
          )}
        >
          <OnboardingTutorial
            clubs={clubs}
            onComplete={handleOnboardingComplete}
          />
        </Sentry.ErrorBoundary>
      )}
      {/* Header */}
      <header className="bg-background/70 backdrop-blur-xl border-b border-border/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-display font-bold tracking-tight">HalftimeScout</span>
          </div>

          <div className="flex items-center gap-2">
            {profile && (
              <button
                onClick={() => navigate('/profile')}
                className="hidden sm:flex items-center gap-3 mr-2 px-3 py-1.5 rounded-full border border-border/60 bg-card hover:border-primary/30 hover:bg-surface transition-colors group"
                aria-label="Open profile"
              >
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                  <span className="num text-xs font-medium text-muted-foreground">Lv.{level}</span>
                </div>
                <div className="h-3 w-px bg-border" />
                <span className="text-xs font-medium group-hover:text-primary transition-colors">{profile.full_name}</span>
                <div className="h-6 w-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold">
                  {(profile.full_name || 'S').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                </div>
              </button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
              className="sm:hidden text-muted-foreground hover:text-foreground"
              aria-label="Open profile"
            >
              <User className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} aria-label="Settings">
              <Settings className="h-4 w-4" />
            </Button>
            <AdminNotificationBell />
            <ThemeToggle />
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="gap-1.5 text-primary hover:text-primary"
                aria-label="Admin panel"
              >
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline text-xs font-semibold">Admin</span>
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={signOut} className="text-muted-foreground hover:text-foreground" aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Hero Section */}
        <motion.div
          className="relative isolate overflow-hidden rounded-2xl gradient-hero p-6 sm:p-8 text-primary-foreground shadow-elevated ring-1 ring-inset ring-primary-foreground/10 grid gap-x-10 gap-y-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_80%_0%,hsl(var(--primary-glow)/0.35),transparent_65%)]" />

          {/* Add Player CTA - absolute on desktop (top-right of hero) */}
          <div className="hidden sm:flex order-2 z-10 gap-2 lg:col-start-2 lg:row-start-1 lg:justify-end">
            <Button
              onClick={() => navigate('/scout')}
              className="h-11 gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold shadow-lg"
            >
              <Target className="h-4 w-4" />
              Scout Player
            </Button>
            <Button
              onClick={() => navigate('/add-player')}
              variant="outline"
              className="h-11 gap-2 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-semibold"
            >
              <UserPlus className="h-4 w-4" />
              Add New Player
            </Button>
          </div>

          <div className="relative z-10 order-1 lg:col-start-1 lg:row-start-1 lg:row-span-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] opacity-80 mb-1.5">Welcome back</p>
            <h1 className="text-3xl md:text-5xl font-display font-bold tracking-tight mb-2">
              {profile?.full_name || 'Scout'}
            </h1>
            <p className="text-sm opacity-70 max-w-md">
              Ready to discover your next star? Search the database or start a scouting session.
            </p>

            {/* Add Player CTA - inline on mobile (stacked below welcome text) */}
            <Button
              onClick={() => navigate('/scout')}
              className="sm:hidden mt-4 h-11 w-full gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold shadow-lg"
            >
              <Target className="h-4 w-4" />
              Scout Player
            </Button>
            <Button
              onClick={() => navigate('/add-player')}
              variant="outline"
              className="sm:hidden mt-2 h-11 w-full gap-2 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-semibold"
            >
              <UserPlus className="h-4 w-4" />
              Add New Player
            </Button>
          </div>

          {/* XP Bar embedded in hero */}
          <div className="relative z-10 order-3 mt-6 max-w-sm lg:mt-0 lg:max-w-none lg:col-start-2 lg:row-start-2">
            <div className="flex items-center justify-between text-xs mb-1.5 opacity-80">
              <span className="font-medium">Level {level}</span>
              <span className="num">{profile?.xp || 0} XP</span>
            </div>
            <div className="h-2 rounded-full bg-primary-foreground/15 overflow-hidden ring-1 ring-inset ring-primary-foreground/10">
              <motion.div
                className="h-full rounded-full bg-primary-foreground/90 shadow-[0_0_12px_2px_hsl(var(--primary-foreground)/0.45)]"
                initial={{ width: 0 }}
                animate={{ width: `${progress.percent}%` }}
                transition={{ duration: 1, delay: 0.3 }}
              />
            </div>
            {level < MAX_LEVEL && (
              <p className="text-[10px] opacity-60 mt-1">
                {progress.next - (profile?.xp || 0)} XP to Level {level + 1}
              </p>
            )}
          </div>

          {/* Weekly Goal strip */}
          <div className="relative z-10 order-4 mt-5 max-w-sm lg:mt-0 lg:max-w-none lg:col-start-2 lg:row-start-3">
            <div className="flex items-center gap-1.5 text-xs mb-1.5 opacity-90">
              <Flame className="h-3.5 w-3.5" />
              <span>
                This week's goal — <span className="font-semibold">{scoutedThisWeek}</span> of {WEEKLY_GOAL} players scouted
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-primary-foreground/15 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-primary-foreground"
                initial={{ width: 0 }}
                animate={{ width: `${weeklyPercent}%` }}
                transition={{ duration: 0.8, delay: 0.4 }}
              />
            </div>
          </div>
        </motion.div>

        {/* Continue scouting */}
        {openSession && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-primary/30 bg-primary/5 shadow-glow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-primary/15 text-primary motion-safe:animate-pulse-ring">
                  <Play className="h-4 w-4" fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    Continue scouting — {openSession.player_name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {openSession.ratings_count} of {TOTAL_ATTRIBUTES} rated
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(`/scout/${openSession.player_id}`)}
                  className="h-9 gap-1"
                >
                  Resume
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Row */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        >
          {[
            { label: 'Players', value: playerCount, icon: Users, accent: 'text-primary' },
            { label: 'Scouted', value: scoutedCount, icon: Shield, accent: scoutedCount > 0 ? 'text-primary' : 'text-muted-foreground' },
            { label: 'Level', value: level, icon: Target, accent: 'text-primary' },
            { label: 'Achievements', value: badgesCount, icon: Award, accent: badgesCount > 0 ? 'text-primary' : 'text-muted-foreground' },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0 } }}
            >
              <Card className="h-full transition-colors hover:border-primary/30">
                <CardContent className="p-5 flex flex-col items-center justify-center text-center gap-2">
                  <div className={`${stat.accent} h-10 w-10 flex items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/15`}>
                    <stat.icon className="h-5 w-5" />
                  </div>
                  <p className="num-display text-3xl font-bold leading-none">{stat.value}</p>
                  <p className="eyebrow">{stat.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>


        {/* Featured player */}
        {featured.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg md:text-xl font-display font-bold">Featured player</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featured.slice(0, 3).map((f) => (
                <Card
                  key={f.id}
                  className={`group transition-all duration-300 h-full ${
                    f.slot === 0 ? 'border-primary/40 ring-1 ring-primary/30 shadow-glow' : 'opacity-75 shadow-none'
                  }`}
                >
                  <CardContent className="p-5 flex h-full flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <ClubBadge club={f.club} />
                        <div className="min-w-0">
                          <h3 className="font-display font-semibold text-[15px] leading-tight truncate">
                            {f.full_name}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{f.club}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`num-display inline-flex items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20 font-bold ${f.slot === 0 ? 'h-10 min-w-10 px-2 text-xl' : 'h-8 min-w-8 px-1.5 text-sm'}`}>{f.overall}</span>
                          <Badge variant="outline" className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${getPositionStyle(f.player_position)}`}>
                            {f.player_position.toUpperCase()}
                          </Badge>
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {f.slot === 0 ? 'Today' : f.slot === 1 ? `Next in ${timeUntilMidnight()}` : `In ${f.slot} days`}
                        </span>
                      </div>

                    </div>


                    <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
                      <div className="rounded-lg bg-surface px-2.5 py-1.5">
                        <span className="num block text-foreground font-semibold text-sm">{calculateAge(f.date_of_birth)}</span>
                        <span>Age</span>
                      </div>
                      
                      <div className="rounded-lg bg-surface px-2.5 py-1.5 min-w-0">
                        <span className="block truncate text-foreground font-semibold text-sm">{f.nationality}</span>
                        <span>Nationality</span>
                      </div>
                      <div className="shrink-0 [&>div]:mt-0 [&>div]:gap-1.5 [&_button]:h-7 [&_button]:w-7 [&_button]:shadow-none [&_a]:h-7 [&_a]:w-7 [&_svg]:h-3.5 [&_svg]:w-3.5">
                        <PlayerBadges playerId={f.id} max={3} />
                      </div>
                    </div>


                    {f.slot === 0 ? (
                      <div className="flex gap-2">
                        {featuredOnCooldown ? (
                          <Button size="sm" disabled className="flex-1 h-9 text-xs gap-1" title="Already scouted today">
                            <Check className="h-3 w-3" />
                            Scouted today
                          </Button>
                        ) : (
                          <Button asChild size="sm" className="flex-1 h-9 text-xs gap-1">
                            <Link to={`/scout/${f.id}?mode=comparison-anchors`} aria-label={`Scout ${f.full_name}`}>
                              Scout
                              <ChevronRight className="h-3 w-3" />
                            </Link>
                          </Button>
                        )}
                        <Button asChild variant="ghost" size="sm" className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground">
                          <Link to={`/player/${f.id}`} aria-label={`View ${f.full_name} profile`}>
                            Profile
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-auto h-9 flex items-center justify-center rounded-lg bg-surface text-xs text-muted-foreground">
                        Coming up
                      </div>
                    )}

                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Discover players to scout */}
        <section className="space-y-3">
          <h2 className="text-lg md:text-xl font-display font-bold">Discover players to scout</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout" initial={false}>
              {discoverSlots.map((slot) => (
                <motion.div
                  key={slot.player.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                >
                  <Card className="group h-full transition-[transform,border-color,box-shadow] duration-[220ms] ease-out hover:-translate-y-0.5 hover:shadow-elevated hover:border-primary/30">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <ClubBadge club={slot.player.club} />
                          <div className="min-w-0">
                            <h3 className="font-display font-semibold text-[15px] leading-tight truncate">
                              {slot.player.full_name}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">{slot.player.club}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {discoverOveralls[slot.player.id] != null && (
                            <span className="num-display inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-primary/10 px-1.5 text-sm font-bold text-primary ring-1 ring-primary/20">
                              {discoverOveralls[slot.player.id]}
                            </span>
                          )}
                          <Badge variant="outline" className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${getPositionStyle(slot.player.position)}`}>
                            {slot.player.position.toUpperCase()}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
                        <div className="rounded-lg bg-surface px-2.5 py-1.5">
                          <span className="num block text-foreground font-semibold text-sm">{calculateAge(slot.player.date_of_birth)}</span>
                          <span>Age</span>
                        </div>
                        
                        <div className="rounded-lg bg-surface px-2.5 py-1.5 min-w-0">
                          <span className="block truncate text-foreground font-semibold text-sm">{slot.player.nationality}</span>
                          <span>Nationality</span>
                        </div>
                        <div className="shrink-0 [&>div]:mt-0 [&>div]:gap-1.5 [&_button]:h-7 [&_button]:w-7 [&_button]:shadow-none [&_a]:h-7 [&_a]:w-7 [&_svg]:h-3.5 [&_svg]:w-3.5">
                          <PlayerBadges playerId={slot.player.id} max={3} />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button asChild size="sm" className="flex-1 h-9 text-xs gap-1">
                          <Link to={`/scout/${slot.player.id}`} aria-label={`Scout ${slot.player.full_name}`}>
                            Scout
                            <ChevronRight className="h-3 w-3" />
                          </Link>
                        </Button>
                        <Button asChild variant="ghost" size="sm" className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground">
                          <Link to={`/player/${slot.player.id}`} aria-label={`View ${slot.player.full_name} profile`}>
                            Profile
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </section>

        <div className="mt-12 pt-6 border-t border-border/60 text-center text-xs text-muted-foreground/80">
          <p>&copy; {new Date().getFullYear()} Halftime Scout</p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <a href="/faq.html" className="hover:text-foreground transition-colors">FAQ</a>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <a
              href="https://instagram.com/halftimescout"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:text-foreground transition-colors"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href="https://x.com/halftimescout"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
              className="hover:text-foreground transition-colors"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
